import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Loader2, Eye, TrendingUp, Users, Link as LinkIcon } from "lucide-react";

/**
 * Admin Analytics panel — shows aggregate page-view stats and per-customer
 * visit summary (visit count, origin UTM tags, last visit time).
 */
export default function AnalyticsPanel() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      api.get(`/analytics/summary?days=${days}`).then((r) => r.data),
      api.get(`/analytics/customers?limit=100`).then((r) => r.data?.customers || []),
    ])
      .then(([s, c]) => {
        if (!mounted) return;
        setSummary(s);
        setCustomers(c);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [days]);

  if (loading || !summary) return <Loader2 className="w-6 h-6 animate-spin text-apex-gold" />;

  return (
    <div data-testid="analytics-panel">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl text-apex-cream font-bold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-apex-gold" />
            Návštěvy webu
          </h3>
          <p className="text-apex-cream/60 text-sm mt-1">Pageviews, zdroje návštěvnosti a aktivita zákazníků.</p>
        </div>
        <div className="flex gap-1.5" data-testid="analytics-range">
          {[7, 14, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-xs border ${
                days === d ? "bg-apex-gold text-apex-bg border-apex-gold" : "bg-apex-card text-apex-cream/75 border-apex-line hover:border-apex-gold/40"
              }`}
              data-testid={`analytics-range-${d}`}
            >
              {d} dní
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <Card icon={Eye} label="Zobrazení stránek" value={summary.totalViews} />
        <Card icon={Users} label="Unikátní návštěvy" value={summary.uniqueSessions} />
        <Card icon={LinkIcon} label="Top zdroj" value={summary.topReferers?.[0]?.referer || "—"} small />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <ListPanel title="Top stránky" items={summary.topPaths?.map((p) => ({ label: p.path, value: p.count })) || []} testId="analytics-top-paths" />
        <ListPanel title="Zdroje návštěvnosti" items={summary.topReferers?.map((r) => ({ label: r.referer, value: r.count })) || []} testId="analytics-top-referers" />
      </div>

      {summary.utmSources?.length > 0 && (
        <div className="mb-6">
          <ListPanel title="UTM kampaně (utm_source)" items={summary.utmSources.map((u) => ({ label: u.source, value: u.count }))} testId="analytics-utm" />
        </div>
      )}

      <div className="rounded-2xl bg-apex-card border border-apex-line p-5">
        <div className="text-xs uppercase tracking-[0.3em] text-apex-gold/80 mb-3">Zákazníci · poslední aktivita</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="analytics-customers-table">
            <thead className="text-left text-apex-cream/60 text-xs uppercase tracking-wider">
              <tr className="border-b border-apex-line">
                <th className="py-2">Jméno</th>
                <th>E-mail</th>
                <th>Návštěvy</th>
                <th>Origin</th>
                <th>Poslední</th>
              </tr>
            </thead>
            <tbody className="text-apex-cream">
              {customers.length === 0 && (
                <tr><td colSpan={5} className="py-4 text-apex-cream/55">Zatím žádné zaznamenané návštěvy.</td></tr>
              )}
              {customers.map((c) => (
                <tr key={c.id} className="border-b border-apex-line/50 hover:bg-apex-card/50">
                  <td className="py-2">{c.firstName} {c.lastName}</td>
                  <td className="text-apex-cream/80">{c.email}</td>
                  <td className="font-semibold text-apex-gold">{c.visitCount || 0}</td>
                  <td className="text-xs text-apex-cream/70">{formatUtm(c.originUtm)}</td>
                  <td className="text-xs text-apex-cream/60">{formatRel(c.lastVisitAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, small = false }) {
  return (
    <div className="rounded-2xl bg-apex-card border border-apex-line p-4">
      <div className="w-9 h-9 rounded-lg bg-apex-gold/15 text-apex-gold flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className={`mt-2 font-display text-apex-cream font-bold ${small ? "text-base truncate" : "text-2xl"}`} title={typeof value === "string" ? value : undefined}>
        {typeof value === "number" ? value.toLocaleString("cs-CZ") : value}
      </div>
      <div className="text-xs text-apex-cream/60 uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function ListPanel({ title, items, testId }) {
  return (
    <div className="rounded-2xl bg-apex-card border border-apex-line p-5" data-testid={testId}>
      <div className="text-xs uppercase tracking-[0.3em] text-apex-gold/80 mb-3">{title}</div>
      {items.length === 0 && <div className="text-sm text-apex-cream/50">Žádná data.</div>}
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-center justify-between text-sm">
            <span className="text-apex-cream/85 truncate pr-2">{it.label || "—"}</span>
            <span className="font-mono text-apex-gold">{it.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatUtm(utm) {
  if (!utm || !Object.keys(utm).length) return "—";
  const parts = [];
  if (utm.source) parts.push(utm.source);
  if (utm.medium) parts.push(utm.medium);
  if (utm.campaign) parts.push(`(${utm.campaign})`);
  return parts.join(" · ");
}

function formatRel(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const hrs = Math.floor(diffMs / 3_600_000);
    if (hrs < 1) return "před chvílí";
    if (hrs < 24) return `před ${hrs} h`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `před ${days} dny`;
    return d.toLocaleDateString("cs-CZ");
  } catch {
    return iso;
  }
}
